import { useCallback, useEffect, useState } from 'react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts'
import clsx from 'clsx'
import { TrendingUp, TrendingDown, RefreshCw, Clock, AlertTriangle } from 'lucide-react'
import { fetchAccuracyStats, fetchTradeStats } from '../../api/client'
import type { AccuracyStats, TradeLogStats, TradeLogRecord, TradeLogPendingRecord } from '../../types'
import { formatPriceWithSymbol } from '../../types'

// ── 統計バッジ ─────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }: {
  label: string
  value: string
  sub?:  string
  color?: string
}) {
  return (
    <div className="trading-card text-center flex flex-col gap-1">
      <div className="stat-label">{label}</div>
      <div className={clsx('text-2xl font-bold font-mono tabular-nums', color ?? 'text-trading-text')}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-trading-muted">{sub}</div>}
    </div>
  )
}

// ── キャリブレーションチャート ─────────────────────────────────────────────

function CalibrationChart({ data, shadowData }: {
  data: AccuracyStats['calibration']
  shadowData?: AccuracyStats['calibration_shadow']
}) {
  if (!data.length) {
    return <div className="flex items-center justify-center h-32 text-xs text-trading-muted">データなし</div>
  }

  // シャドー(補正後)側はバケット単位で引けるようlookupにしておく
  const shadowByBucket = new Map((shadowData ?? []).map((s) => [s.bucket, s]))

  // 理想線用データ（予測確率 = 実際の上昇率）
  const chartData = data.map((d) => ({
    ...d,
    ideal: d.bucket * 100,
    shadow_rate: shadowByBucket.get(d.bucket)?.actual_rate,
  }))

  return (
    <ResponsiveContainer width="100%" height={180}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 9, fill: '#64748b', fontFamily: 'monospace' }}
          tickLine={false}
          axisLine={{ stroke: '#1f2937' }}
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 9, fill: '#64748b', fontFamily: 'monospace' }}
          tickLine={false}
          axisLine={false}
          width={36}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null
            const d = payload[0].payload
            return (
              <div className="bg-trading-panel border border-trading-border rounded-lg p-2 text-xs font-mono shadow-xl">
                <div className="text-trading-muted mb-1">予測確率 {label}</div>
                <div className="text-trading-blue">実際の上昇率: {d.actual_rate}%</div>
                {d.shadow_rate != null && (
                  <div className="text-trading-yellow">補正後(シャドー): {d.shadow_rate}%</div>
                )}
                <div className="text-trading-muted">理想値: {d.ideal}%</div>
                <div className="text-trading-muted">サンプル数: {d.count}</div>
              </div>
            )
          }}
        />
        <Bar dataKey="actual_rate" radius={[3, 3, 0, 0]} maxBarSize={32}>
          {chartData.map((entry) => (
            <Cell
              key={entry.label}
              fill={entry.actual_rate >= entry.ideal ? '#00d4aa' : '#ff4d6d'}
              fillOpacity={0.8}
            />
          ))}
        </Bar>
        {/* 理想線（予測確率 = 実際の上昇率） */}
        <Line
          dataKey="ideal"
          dot={false}
          stroke="#64748b"
          strokeDasharray="4 2"
          strokeWidth={1.5}
          legendType="none"
          label={false}
        />
        {/* 補正後(シャドー、記録専用)。rise_prob_calが埋まっている行のみ表示される */}
        {shadowData && shadowData.length > 0 && (
          <Line
            dataKey="shadow_rate"
            dot={{ r: 3, fill: '#f5c518' }}
            stroke="#f5c518"
            strokeWidth={1.5}
            connectNulls
            legendType="none"
            label={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

// ── 取引結果行 ─────────────────────────────────────────────────────────────

const OUTCOME_LABEL: Record<TradeLogRecord['outcome'], string> = {
  tp_hit:       '利確',
  sl_hit:       '損切',
  timeout_win:  '期限内(勝)',
  timeout_loss: '期限内(負)',
}

function formatTradeDateTime(ms: number): string {
  return new Date(ms).toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  })
}

function TradeHistoryRow({ r }: { r: TradeLogRecord }) {
  const isWin = r.outcome === 'tp_hit' || r.outcome === 'timeout_win'
  const isBuy = r.direction === 'BUY'
  // 矢印の向きは勝敗ではなく実際の価格の上下で決める(色で勝敗を示す)。
  // SELLは価格下落が勝ちなので、勝敗をそのまま矢印の向きに使うと
  // 実際の値動きと矢印が逆転して見えてしまうため。
  const priceRose = r.exit_price > r.entry_price

  return (
    <div className={clsx(
      'grid gap-2 px-3 py-2 text-[10px] font-mono rounded border-l-2 border border-trading-border',
      isWin ? 'border-l-trading-green bg-trading-green/5' : 'border-l-trading-red bg-trading-red/5',
    )}
    style={{ gridTemplateColumns: TRADE_GRID_COLUMNS }}
    >
      <span className="text-trading-text font-bold truncate">{r.symbol}</span>
      <span className="text-trading-muted tabular-nums">{formatTradeDateTime(r.resolved_at)}</span>
      <span className={clsx('font-bold', isBuy ? 'text-trading-green' : 'text-trading-red')}>
        {r.direction}
      </span>
      <span className="text-trading-muted tabular-nums">{formatPriceWithSymbol(r.entry_price, r.symbol)}</span>
      <span className="text-trading-green/80 tabular-nums">{formatPriceWithSymbol(r.take_profit, r.symbol)}</span>
      <span className="text-trading-red/80 tabular-nums">{formatPriceWithSymbol(r.stop_loss, r.symbol)}</span>
      <span className="text-trading-text tabular-nums">{formatPriceWithSymbol(r.exit_price, r.symbol)}</span>
      <span className={clsx('tabular-nums font-bold', r.pnl_pct >= 0 ? 'text-trading-green' : 'text-trading-red')}>
        {r.pnl_pct >= 0 ? '+' : ''}{(r.pnl_pct * 100).toFixed(2)}%
      </span>
      <span className="flex items-center justify-center" title={OUTCOME_LABEL[r.outcome]}>
        {priceRose
          ? <TrendingUp size={10} className={isWin ? 'text-trading-green' : 'text-trading-red'} />
          : <TrendingDown size={10} className={isWin ? 'text-trading-green' : 'text-trading-red'} />
        }
      </span>
    </div>
  )
}

// 決済済み・エントリー中どちらのテーブルも同じ列幅にして、横スクロールが
// ヘッダー行とデータ行でズレないようにする。
const TRADE_GRID_COLUMNS = '78px 78px 40px 84px 84px 84px 84px 62px 16px'

function PendingTradeRow({ r }: { r: TradeLogPendingRecord }) {
  const isBuy = r.direction === 'BUY'

  return (
    <div
      className="grid gap-2 px-3 py-2 text-[10px] font-mono rounded border-l-2 border border-trading-border border-l-trading-blue bg-trading-blue/5"
      style={{ gridTemplateColumns: TRADE_GRID_COLUMNS }}
    >
      <span className="text-trading-text font-bold truncate">{r.symbol}</span>
      <span className="text-trading-muted tabular-nums">{formatTradeDateTime(r.recorded_at)}</span>
      <span className={clsx('font-bold', isBuy ? 'text-trading-green' : 'text-trading-red')}>
        {r.direction}
      </span>
      <span className="text-trading-muted tabular-nums">{formatPriceWithSymbol(r.entry_price, r.symbol)}</span>
      <span className="text-trading-green/80 tabular-nums">{formatPriceWithSymbol(r.take_profit, r.symbol)}</span>
      <span className="text-trading-red/80 tabular-nums">{formatPriceWithSymbol(r.stop_loss, r.symbol)}</span>
      <span className="text-trading-muted tabular-nums" title="解決予定日時">{formatTradeDateTime(r.target_at)}</span>
      <span className="text-trading-blue tabular-nums font-bold" title="シグナル発生時のAI上昇確率(勝率ではありません)">
        {(r.rise_prob * 100).toFixed(0)}%
      </span>
      <span className="flex items-center justify-center" title="エントリー中">
        <span className="w-1.5 h-1.5 rounded-full bg-trading-blue animate-pulse" />
      </span>
    </div>
  )
}

// ── 精度劣化アラート ────────────────────────────────────────────────────────

function DegradedIntervalWarning({ byInterval }: { byInterval: AccuracyStats['by_interval'] }) {
  const degraded = byInterval.filter((iv) => iv.total >= 20 && iv.accuracy_pct < 50)
  if (!degraded.length) return null

  return (
    <div className="flex items-start gap-2 bg-trading-yellow/10 border border-trading-yellow/30 rounded-lg p-3 text-[11px]">
      <AlertTriangle size={14} className="text-trading-yellow mt-0.5 shrink-0" />
      <div>
        <span className="font-bold text-trading-yellow">精度劣化を検出</span>
        <span className="text-trading-muted ml-1">
          {degraded.map((iv) => `${iv.interval.toUpperCase()}足 (${iv.accuracy_pct}%)`).join(', ')} がランダム以下です。
          キャリブレーション補正またはモデル再チューニングを推奨します。
        </span>
      </div>
    </div>
  )
}

// ── メインコンポーネント ───────────────────────────────────────────────────

export default function AccuracyPanel() {
  const [stats, setStats]           = useState<AccuracyStats | null>(null)
  const [tradeStats, setTradeStats] = useState<TradeLogStats | null>(null)
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const [filterIv, setFilterIv]     = useState<string>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, trades] = await Promise.all([
        fetchAccuracyStats({
          // 「全足」は1h/4h/1dの生の個別予測を合算したもの。ensemble(実際の
          // 売買判定に使われる加重平均後の予測)は独立したトグルで見るため、
          // 「全足」には明示的に含めない(2026-07、interval省略=フィルタなしだと
          // ensembleも無条件に混ざってしまうため、明示的なリストに変更した)。
          interval: filterIv === 'all' ? '1h,4h,1d' : filterIv,
          limit:    100,
        }),
        // 取引結果は常にensembleシグナルのみを対象にする。
        // 1H/4H/1D個別足の取引記録は移行前の旧方式の名残(廃止済み)であり、
        // 予測精度パネルの足フィルタ(全足/1H/4H/1D)とは無関係に固定する。
        fetchTradeStats({
          interval: 'ensemble',
          limit:    100,
        }),
      ])
      setStats(data)
      setTradeStats(trades)
    } catch {
      setError('データ取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [filterIv])

  useEffect(() => { load() }, [load])

  const accuracyColor =
    !stats ? 'text-trading-muted'
    : stats.dir_accuracy_pct >= 60 ? 'text-trading-green'
    : stats.dir_accuracy_pct >= 45 ? 'text-trading-yellow'
    : 'text-trading-red'

  const medianErrColor =
    !stats ? 'text-trading-muted'
    : stats.avg_median_err_pct < 1   ? 'text-trading-green'
    : stats.avg_median_err_pct < 3   ? 'text-trading-yellow'
    : 'text-trading-red'

  return (
    <div className="flex flex-col gap-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-trading-text">AI 予測精度レポート</h2>
          <p className="text-[10px] text-trading-muted mt-0.5">
            過去の予測と実際の結果を自動記録・集計
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* 時間足フィルタ */}
          {(['all', '1h', '4h', '1d', 'ensemble'] as const).map((iv) => (
            <button
              key={iv}
              onClick={() => setFilterIv(iv)}
              className={clsx(
                'text-[10px] font-bold uppercase px-2 py-0.5 rounded border transition-colors',
                filterIv === iv
                  ? 'bg-trading-blue/20 text-trading-blue border-trading-blue/40'
                  : 'text-trading-muted border-trading-border hover:text-trading-text',
              )}
            >
              {iv === 'all' ? '全足' : iv === 'ensemble' ? 'アンサンブル' : iv.toUpperCase()}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            className="text-trading-muted hover:text-trading-text transition-colors"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-trading-red bg-trading-red/10 border border-trading-red/20 rounded p-2">
          {error}
        </div>
      )}

      {/* データなし（まだ記録が溜まっていない） */}
      {stats && stats.total === 0 && !loading && (
        <div className="trading-card flex flex-col items-center gap-3 py-10">
          <Clock size={32} className="text-trading-muted/40" />
          <div className="text-sm text-trading-muted text-center">
            精度データ収集中です
          </div>
          <div className="text-[11px] text-trading-muted/70 text-center max-w-xs leading-relaxed">
            予測が記録され、24 時間後に実価格と照合されます。
            {stats.pending > 0 && ` 現在 ${stats.pending} 件の予測が解決待ちです。`}
          </div>
        </div>
      )}

      {stats && stats.total > 0 && (
        <>
          {/* 精度劣化アラート */}
          <DegradedIntervalWarning byInterval={stats.by_interval} />

          {/* サマリ統計 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="方向精度"
              value={`${stats.dir_accuracy_pct}%`}
              sub={`${stats.dir_correct} / ${stats.total} 件`}
              color={accuracyColor}
            />
            <StatCard
              label="中央値誤差"
              value={`${stats.avg_median_err_pct}%`}
              sub="実価格との平均乖離"
              color={medianErrColor}
            />
            <StatCard
              label="P10–P90 カバレッジ"
              value={`${stats.coverage_pct}%`}
              sub="実価格が予測帯内に収まった割合"
              color={stats.coverage_pct >= 80 ? 'text-trading-green' : 'text-trading-yellow'}
            />
            <StatCard
              label="解決待ち"
              value={`${stats.pending}`}
              sub="24h 後に自動解決"
              color="text-trading-muted"
            />
          </div>

          {/* キャリブレーション + 時間足別 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="trading-card">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted mb-3">
                キャリブレーション
                <span className="ml-2 text-[9px] font-normal normal-case">予測確率 vs 実際の上昇率</span>
              </h3>
              <CalibrationChart data={stats.calibration} shadowData={stats.calibration_shadow} />
              <p className="text-[9px] text-trading-muted/60 mt-2 text-right">
                棒が点線（理想）に近いほど予測が正確
              </p>
              {stats.shadow_total > 0 && (
                <p className="text-[9px] text-trading-yellow/80 mt-1 text-right">
                  補正後(シャドー): Brier {stats.brier_raw?.toFixed(4)} → {stats.brier_cal?.toFixed(4)}
                  {' '}(n={stats.shadow_total})
                </p>
              )}
            </div>

            <div className="trading-card flex flex-col gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted">
                サイン別 · シンボル別 精度
              </h3>

              {/* BUY/SELL 方向別 */}
              {stats.by_direction.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {stats.by_direction.map((d) => {
                    const color = d.accuracy_pct >= 60 ? 'bg-trading-green' : d.accuracy_pct >= 45 ? 'bg-trading-yellow' : 'bg-trading-red'
                    const textColor = d.accuracy_pct >= 60 ? 'text-trading-green' : d.accuracy_pct >= 45 ? 'text-trading-yellow' : 'text-trading-red'
                    return (
                      <div key={d.direction}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={clsx('text-[10px] font-bold', d.direction === 'BUY' ? 'text-trading-green' : 'text-trading-red')}>
                            {d.direction}
                          </span>
                          <span className={clsx('text-[10px] font-mono font-bold', textColor)}>
                            {d.accuracy_pct}% <span className="text-trading-muted font-normal">({d.total}件)</span>
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-trading-bg rounded-full overflow-hidden">
                          <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${d.accuracy_pct}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="divider" />

              {/* シンボル別 上位6件 */}
              <div className="flex flex-col gap-1">
                {stats.by_symbol.slice(0, 6).map((s) => {
                  const textColor = s.accuracy_pct >= 60 ? 'text-trading-green' : s.accuracy_pct >= 45 ? 'text-trading-yellow' : 'text-trading-red'
                  return (
                    <div key={s.symbol} className="flex items-center justify-between">
                      <span className="text-[10px] font-mono text-trading-text">{s.symbol}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1 bg-trading-bg rounded-full overflow-hidden">
                          <div
                            className={clsx('h-full rounded-full', s.accuracy_pct >= 60 ? 'bg-trading-green' : s.accuracy_pct >= 45 ? 'bg-trading-yellow' : 'bg-trading-red')}
                            style={{ width: `${s.accuracy_pct}%` }}
                          />
                        </div>
                        <span className={clsx('text-[10px] font-mono font-bold w-10 text-right', textColor)}>
                          {s.accuracy_pct}%
                        </span>
                        <span className="text-[9px] text-trading-muted w-8">{s.total}件</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* 取引結果（適応的BUY/SELLシグナルの模擬取引） */}
          {tradeStats && (tradeStats.history.length > 0 || tradeStats.pending_trades.length > 0) && (
            <div className="trading-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted">
                  直近の取引結果
                  <span className="ml-2 text-[9px] font-normal normal-case text-trading-muted/70">
                    BUY/SELLシグナル発生時のP25/P75をTP/SLとした模擬取引
                  </span>
                </h3>
                <div className="flex items-center gap-3 text-[10px] font-mono">
                  <span className="text-trading-muted">
                    勝率
                    <span className={clsx(
                      'ml-1 font-bold',
                      tradeStats.win_rate_pct >= 60 ? 'text-trading-green'
                        : tradeStats.win_rate_pct >= 45 ? 'text-trading-yellow'
                        : 'text-trading-red',
                    )}>
                      {tradeStats.win_rate_pct}%
                    </span>
                    <span className="text-trading-muted/60"> ({tradeStats.wins}/{tradeStats.total})</span>
                  </span>
                  <span className="text-trading-muted">
                    平均損益
                    <span className={clsx('ml-1 font-bold', tradeStats.avg_pnl_pct >= 0 ? 'text-trading-green' : 'text-trading-red')}>
                      {tradeStats.avg_pnl_pct >= 0 ? '+' : ''}{tradeStats.avg_pnl_pct}%
                    </span>
                  </span>
                  {tradeStats.pending > 0 && (
                    <span className="text-trading-muted">保有中 {tradeStats.pending}件</span>
                  )}
                </div>
              </div>

              <p className="text-[9px] text-trading-muted/60 mb-3">
                ※ 保有期間は資産クラス別に固定(暗号資産48時間・FX24時間)。期限までにTP/SLへ未到達の場合は、期限到達時点の価格で自動決済されます(下表の「期限内(勝)」「期限内(負)」)。各シグナルの具体的な解決予定日時は「解決予定」列を参照してください。
              </p>

              {/* BUY/SELL 方向別 勝率（実際のアンサンブルシグナルの成績） */}
              {tradeStats.by_direction.length > 0 && (
                <div className="flex items-center gap-4 mb-4 text-[10px] font-mono">
                  {tradeStats.by_direction.map((d) => (
                    <span key={d.direction} className="flex items-center gap-1.5">
                      <span className={clsx('font-bold', d.direction === 'BUY' ? 'text-trading-green' : 'text-trading-red')}>
                        {d.direction}
                      </span>
                      <span className={clsx(
                        'font-bold',
                        d.win_rate_pct >= 60 ? 'text-trading-green'
                          : d.win_rate_pct >= 45 ? 'text-trading-yellow'
                          : 'text-trading-red',
                      )}>
                        勝率 {d.win_rate_pct}%
                      </span>
                      <span className="text-trading-muted">({d.total}件)</span>
                      <span className={clsx('text-trading-muted', d.avg_pnl_pct >= 0 ? 'text-trading-green/80' : 'text-trading-red/80')}>
                        平均{d.avg_pnl_pct >= 0 ? '+' : ''}{d.avg_pnl_pct}%
                      </span>
                    </span>
                  ))}
                </div>
              )}

              {/* エントリー中（未解決）のシグナル */}
              {tradeStats.pending_trades.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-trading-blue animate-pulse" />
                    <span className="text-[10px] font-semibold text-trading-blue uppercase tracking-wider">
                      エントリー中 {tradeStats.pending_trades.length}件
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <div className="min-w-fit">
                      <div
                        className="grid gap-2 px-3 py-1.5 text-[9px] text-trading-muted uppercase tracking-wider"
                        style={{ gridTemplateColumns: TRADE_GRID_COLUMNS }}
                      >
                        <span>シンボル</span>
                        <span>エントリー日時</span>
                        <span>方向</span>
                        <span>エントリ</span>
                        <span>利確(TP)</span>
                        <span>損切(SL)</span>
                        <span>解決予定</span>
                        <span>上昇確率</span>
                        <span></span>
                      </div>
                      <div className="flex flex-col gap-1 mt-1">
                        {tradeStats.pending_trades.map((p) => (
                          <PendingTradeRow key={p.id} r={p} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tradeStats.history.length > 0 && (
                <>
                  {/* 決済パターン別の内訳: tp_hit/sl_hitは経路依存(保有期間中にTP/SLへ先に
                      到達したか)、timeout_win/timeout_lossは終点依存(期限最終足の終値のみで
                      判定、方向精度と同じ基準)。両者が混在するため勝率と方向精度が一致しない */}
                  <div className="flex items-center gap-3 text-[9px] text-trading-muted mb-3">
                    <span>内訳:</span>
                    <span>利確 <span className="text-trading-green font-bold">{tradeStats.outcome_breakdown.tp_hit}</span>件</span>
                    <span>損切 <span className="text-trading-red font-bold">{tradeStats.outcome_breakdown.sl_hit}</span>件</span>
                    <span>期限内(勝) <span className="text-trading-green font-bold">{tradeStats.outcome_breakdown.timeout_win}</span>件</span>
                    <span>期限内(負) <span className="text-trading-red font-bold">{tradeStats.outcome_breakdown.timeout_loss}</span>件</span>
                  </div>
                  <div className="overflow-x-auto">
                    <div className="min-w-fit">
                      {/* ヘッダー行 */}
                      <div
                        className="grid gap-2 px-3 py-1.5 text-[9px] text-trading-muted uppercase tracking-wider"
                        style={{ gridTemplateColumns: TRADE_GRID_COLUMNS }}
                      >
                        <span>シンボル</span>
                        <span>決済日時</span>
                        <span>方向</span>
                        <span>エントリ</span>
                        <span>利確(TP)</span>
                        <span>損切(SL)</span>
                        <span>決済価格</span>
                        <span>損益</span>
                        <span></span>
                      </div>
                      <div className="flex flex-col gap-1 mt-1 max-h-64 overflow-y-auto">
                        {tradeStats.history.map((r) => (
                          <TradeHistoryRow key={r.id} r={r} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-3 text-[9px] text-trading-muted">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-sm bg-trading-green/40 border-l-2 border-trading-green inline-block" />
                        利確 / 期限内決済(勝)
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-sm bg-trading-red/40 border-l-2 border-trading-red inline-block" />
                        損切 / 期限内決済(負)
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[9px] text-trading-muted">
                      <span>BUY=緑 / SELL=赤</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 取引データ待ち */}
          {tradeStats && tradeStats.history.length === 0 && tradeStats.pending_trades.length === 0 && (
            <div className="trading-card flex flex-col items-center gap-2 py-6">
              <div className="text-xs text-trading-muted text-center">
                取引シグナルのデータ収集中です
              </div>
              <div className="text-[10px] text-trading-muted/70 text-center max-w-xs leading-relaxed">
                rise_probability が適応的BUY/SELL閾値を超えると記録されます。
                {tradeStats.pending > 0 && ` 現在 ${tradeStats.pending} 件が保有中です。`}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
